
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/chart/custom'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/toolbox'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'

import TextBox from '@/components/TextBox.vue'
import { roasMantissa } from '@/utils/roasUnits.js'

// Fonts the chart actually draws with, so the widths measured below match what
// ECharts renders: the axis labels inherit textStyle (12px Roboto, weight 400)
// and the mean labels are drawn by renderErrorBar one step smaller.
const AXIS_FONT = '12px "Roboto","Helvetica",sans-serif'
const VALUE_FONT = '11px "Roboto","Helvetica",sans-serif'
// One line of 12px text at the chart's 1.2381 lineHeight.
const LINE_HEIGHT = 15
// ECharts' default axisLabel.margin, i.e. the gap between the axis line and its labels.
const LABEL_MARGIN = 8
const X_LABEL_ROTATE = 40
// Safety valve: a channel name longer than this (~25 characters) is truncated
// with an ellipsis so one pathological pretty name can't swallow the chart. The
// axis tooltip still shows the name in full.
const X_LABEL_MAX_WIDTH = 150
const BASE_HEIGHT = 420
// Rotated-label extent the base height already accounts for; anything beyond it
// grows the box instead of eating into the plot area.
const BASE_LABEL_EXTENT = 60
const MAX_HEIGHT = 520
// Clearance a mean label needs on top of its own width to count as fitting
// inside one category band.
const VALUE_LABEL_CLEARANCE = 8
// Bands kept free outside the tick labels for the axis names. ECharts anchors a
// name at `axis position ∓ nameGap` and the name's box extends 12px into the
// band from there, so each band has to hold that box plus the slack the gaps
// below need (see yAxis/xAxis nameGap).
const Y_NAME_BAND = 40
const X_NAME_BAND = 36
// Kept as module constants so the chart is handed the same objects on every
// render. vue-echarts disposes and rebuilds the chart whenever it sees a new
// init-options object, and replaces the whole option (notMerge) whenever the
// option object is new; either made the bars regrow from zero on each data
// change instead of animating from their previous height to the new one.
const CHART_INIT_OPTIONS = { renderer: 'canvas' }
const CHART_UPDATE_OPTIONS = { notMerge: false }
// Width of `text` in `font`, in pixels, through a single cached 2D context.
// Falls back to a per-character estimate where canvas isn't available.
let measureContext = null
function measureText (text, font) {
  const value = String(text)
  if (measureContext === null) {
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null
    measureContext = (canvas && canvas.getContext) ? canvas.getContext('2d') : false
  }
  if (!measureContext) return value.length * 7
  measureContext.font = font
  return measureContext.measureText(value).width
}

export default {
  computed: {
    chartInitOptions () {
      return CHART_INIT_OPTIONS
    },
    chartUpdateOptions () {
      return CHART_UPDATE_OPTIONS
    },
    channels () {
      return Object.entries(this.data)
    },
    channelNames () {
      return this.channels.map(entry => entry[0])
    },
    barData () {
      return this.channels.map(entry => ({
        value: entry[1].mean,
        itemStyle: {
          color: this.colors && this.colors[entry[0]] ? this.colors[entry[0]] : '#2979c9'
        }
      }))
    },
    errorData () {
      return this.channels.map((entry, index) => [index, entry[1].lower, entry[1].upper])
    },
    // An upper bound on the width of the widest y tick label. ECharts picks the
    // ticks itself, so the bound is derived from the data extent: the rounded-out
    // extreme (carrying its sign and its thousand separators, so it is measured
    // exactly as a tick would be drawn) plus two decimals is at least as wide as
    // any tick formatValue can produce.
    yLabelWidth () {
      let max = 0
      let min = 0
      this.channels.forEach(entry => {
        const values = [entry[1].mean, entry[1].lower, entry[1].upper]
        values.forEach(value => {
          if (typeof value !== 'number' || isNaN(value)) return
          if (value > max) max = value
          if (value < min) min = value
        })
      })
      const candidates = [this.formatValue(Math.ceil(max))]
      if (min < 0) candidates.push(this.formatValue(Math.floor(min)))
      // Decimals the widest tick can carry. Cross-unit ratios can be small enough
      // to need more than two, so the pad follows the same rule the ticks do
      // instead of assuming '.00'.
      const pad = '.' + '0'.repeat(roasMantissa(Math.max(Math.abs(max), Math.abs(min))))
      const widths = candidates.map(candidate => measureText(candidate + pad, AXIS_FONT))
      return Math.ceil(Math.max.apply(null, widths))
    },
    // 'ROAS', or 'ROAS (units/USD)' when the KPI and the spend behind the ratio are
    // measured in different units. It names the y axis and the bar series — the
    // latter is what ECharts' default Data View prints as the column header.
    roasAxisName () {
      return this.unit ? `ROAS (${this.unit})` : 'ROAS'
    },
    kpiName () {
      return this.customKpiName || this.$t('components.ModelReportComponent.defaultKpiName')
    },
    // A ratio carrying a composite unit isn't the familiar return multiple, so the box
    // flags it rather than letting the axis name be the only hint.
    warnings () {
      if (!this.unit) return []
      return [this.$t('components.simulationDetails.RoasByChannel.crossUnitWarning', { kpi: this.kpiName, unit: this.unit })]
    },
    // Width of the longest channel name as drawn, capped at the truncation width.
    xLabelWidth () {
      let widest = 0
      this.channelNames.forEach(name => {
        const width = measureText(name, AXIS_FONT)
        if (width > widest) widest = width
      })
      return Math.min(Math.ceil(widest), X_LABEL_MAX_WIDTH)
    },
    // Vertical room the inclined labels take up: the height of their rotated
    // bounding box.
    xLabelExtent () {
      const radians = X_LABEL_ROTATE * Math.PI / 180
      return Math.ceil(this.xLabelWidth * Math.sin(radians) + LINE_HEIGHT * Math.cos(radians))
    },
    // Long channel names grow the box rather than squeezing the plot area, so
    // short-named models keep exactly the height this chart has always had.
    chartHeight () {
      return Math.min(MAX_HEIGHT, BASE_HEIGHT + Math.max(0, this.xLabelExtent - BASE_LABEL_EXTENT))
    },
    options () {
      const vm = this
      return {
        textStyle: {
          fontFamily: '"Roboto","Helvetica",sans-serif',
          lineHeight: 1.2381,
          fontWeight: 400
        },
        grid: {
          top: 40,
          // containLabel reserves room for the tick labels but not for the axis
          // names, so both margins keep a band free outside the labels: the left
          // one for the rotated 'ROAS' name, the bottom one for 'Channel'. The
          // nameGap of each axis (below) is what places the name inside its band.
          left: Y_NAME_BAND,
          right: '4%',
          bottom: X_NAME_BAND,
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const idx = params[0].dataIndex
            const name = vm.channelNames[idx]
            const entry = vm.channels[idx][1]
            const color = vm.colors && vm.colors[name] ? vm.colors[name] : '#2979c9'
            const row = function (label, value, bold) {
              return '<div style="display:flex;justify-content:space-between;min-width:170px;line-height:1.7;">' +
                '<span style="color:#666;margin-right:20px;">' + label + '</span>' +
                '<span style="font-weight:' + (bold ? '700' : '400') + ';">' + value + '</span>' +
                '</div>'
            }
            return '<div style="display:flex;align-items:center;margin-bottom:4px;">' +
              '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:' + color + ';margin-right:7px;"></span>' +
              '<span style="font-weight:700;">' + name + '</span>' +
              '</div>' +
              row(vm.$t('components.simulationDetails.RoasByChannel.mean'), vm.formatValueWithUnit(entry.mean), true) +
              row(vm.$t('components.simulationDetails.RoasByChannel.lower'), vm.formatValueWithUnit(entry.lower), false) +
              row(vm.$t('components.simulationDetails.RoasByChannel.upper'), vm.formatValueWithUnit(entry.upper), false)
          }
        },
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.simulationDetails.RoasByChannel.dataView'),
              lang: [this.$t('components.simulationDetails.RoasByChannel.dataView'), this.$t('components.simulationDetails.RoasByChannel.close'), this.$t('components.simulationDetails.RoasByChannel.refresh')]
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || 'ROAS by Channel',
              title: this.$t('components.simulationDetails.RoasByChannel.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        xAxis: {
          type: 'category',
          name: this.$t('components.simulationDetails.RoasByChannel.xAxisName'),
          nameLocation: 'middle',
          // Measured from the labels' own extent instead of a fixed guess, which
          // either dropped the name off the bottom of the box or landed it on
          // top of the labels depending on how long the channel names were. The
          // name lands mid-band, so it stays clear of both the labels above and
          // the container edge below even if the extent is a few px out.
          nameGap: this.xLabelExtent + LABEL_MARGIN + 11,
          data: this.channelNames,
          axisLabel: {
            rotate: X_LABEL_ROTATE,
            interval: 0,
            width: X_LABEL_MAX_WIDTH,
            overflow: 'truncate'
          }
        },
        yAxis: {
          type: 'value',
          name: this.roasAxisName,
          nameLocation: 'middle',
          // Cleared past the widest tick label the axis can produce; a fixed gap
          // sat on top of the labels as soon as the values got wider. yLabelWidth
          // is an upper bound, so the name can only drift further from the axis
          // (never onto the labels) — never by more than the '.00' the bound adds,
          // which is what Y_NAME_BAND absorbs.
          nameGap: this.yLabelWidth + 10,
          axisLabel: {
            formatter: this.formatValue
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: this.roasAxisName,
            type: 'bar',
            z: 2,
            barMaxWidth: 60,
            data: this.barData
          },
          {
            name: 'interval',
            type: 'custom',
            z: 3,
            silent: true,
            renderItem: this.renderErrorBar,
            // y: [1, 2] keeps the axis extent including `upper`, so whiskers aren't clipped.
            encode: { x: 0, y: [1, 2] },
            data: this.errorData
          }
        ]
      }
    }
  },
  methods: {
    formatValue (value) {
      return numbro(value).format({
        thousandSeparated: true,
        mantissa: roasMantissa(value),
        optionalMantissa: true,
        trimMantissa: true
      })
    },
    // Tooltip figures carry the composite unit when there is one; the axis ticks
    // and the mean labels stay bare, since the axis name already names it.
    formatValueWithUnit (value) {
      const formatted = this.formatValue(value)
      return this.unit ? `${formatted} ${this.unit}` : formatted
    },
    // Blend a hex color toward white by `amount` (0-1). Non-hex colors pass through.
    lightenColor (color, amount) {
      if (typeof color !== 'string') return color
      const match = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
      if (!match) return color
      let hex = match[1]
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('')
      }
      const channels = [0, 2, 4].map(offset => {
        const value = parseInt(hex.substr(offset, 2), 16)
        return Math.round(value + (255 - value) * amount)
      })
      return 'rgb(' + channels.join(',') + ')'
    },
    renderErrorBar (params, api) {
      // data item: [categoryIndex, lower, upper]
      const xValue = api.value(0)
      const lowPoint = api.coord([xValue, api.value(1)])
      const highPoint = api.coord([xValue, api.value(2)])
      const bandWidth = api.size([1, 0])[0]
      const halfWidth = Math.min(bandWidth * 0.12, 12)
      const channelName = this.channels[params.dataIndex][0]
      const channelColor = this.colors && this.colors[channelName] ? this.colors[channelName] : '#2979c9'
      const style = api.style({
        stroke: this.lightenColor(channelColor, 0.4),
        fill: null,
        lineWidth: 1.5
      })
      const mean = this.channels[params.dataIndex][1].mean
      const meanPoint = api.coord([xValue, mean])
      const meanText = this.formatValue(mean)
      const children = [
        {
          type: 'line',
          transition: ['shape'],
          shape: { x1: highPoint[0], y1: highPoint[1], x2: lowPoint[0], y2: lowPoint[1] },
          style: style
        },
        {
          type: 'line',
          transition: ['shape'],
          shape: { x1: highPoint[0] - halfWidth, y1: highPoint[1], x2: highPoint[0] + halfWidth, y2: highPoint[1] },
          style: style
        },
        {
          type: 'line',
          transition: ['shape'],
          shape: { x1: lowPoint[0] - halfWidth, y1: lowPoint[1], x2: lowPoint[0] + halfWidth, y2: lowPoint[1] },
          style: style
        }
      ]
      // Each label is centred on its own category band, so one that fits inside
      // its band can't reach a neighbour's: where the bars get too tight the
      // crowded labels drop out instead of overprinting each other, and the
      // tooltip still reports the exact value. ECharts' labelLayout/hideOverlap
      // can't do this for us — the label is a hand-drawn zrender node, not a
      // series label (moving it onto the bar series would bury it under the
      // whisker, and reversing the z-order would hide the lower whisker arm).
      if (measureText(meanText, VALUE_FONT) + VALUE_LABEL_CLEARANCE <= bandWidth) {
        children.push({
          // Mean label just above the bar top, drawn last so it sits on top
          // of the whisker line.
          type: 'text',
          x: meanPoint[0],
          y: meanPoint[1] - 6,
          style: {
            text: meanText,
            fill: '#212121',
            // White halo so the value stays legible where it crosses the
            // confidence bar. zrender paints a text stroke before the fill, so
            // this reads as a contour around the glyphs rather than over them.
            stroke: '#FFFFFF',
            lineWidth: 3,
            font: VALUE_FONT,
            align: 'center',
            verticalAlign: 'bottom'
          }
        })
      }
      return {
        type: 'group',
        children: children
      }
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: false
    },
    // Composite unit of the ratio ('units/USD'), empty when the KPI and the spend
    // share a unit and the ROAS is therefore dimensionless.
    unit: {
      type: String,
      required: false,
      default: function () {
        return ''
      }
    },
    // Only used by the cross-unit warning copy; falls back to the generic KPI name.
    customKpiName: {
      type: String,
      required: false,
      default: function () {
        return ''
      }
    },
    exportImageName: {
      type: String,
      required: false
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox
  }
}
